/**
 * Ollama provider adapter for ludicrous-gateway
 * Connects to local Ollama instance for private/local inference
 */

import { BaseProvider } from "./base-provider.js";
import type {
  ProviderRequestContext,
  StreamHandler,
} from "../types/providers.js";
import type { MessagesRequest, MessagesResponse } from "../types/messages.js";
import type { ProviderConfig } from "../types/config.js";
import type { TextContent } from "../types/messages.js";

/**
 * Ollama request format
 */
interface OllamaRequest {
  model: string;
  messages: Array<{
    role: "system" | "user" | "assistant";
    content: string;
  }>;
  stream: boolean;
  options?: {
    temperature?: number | undefined;
    top_p?: number | undefined;
    top_k?: number | undefined;
    num_predict?: number | undefined;
  };
}

/**
 * Ollama response format
 */
interface OllamaResponse {
  model: string;
  created_at: string;
  message: {
    role: "assistant";
    content: string;
  };
  done: boolean;
  total_duration?: number;
  load_duration?: number;
  prompt_eval_count?: number;
  eval_count?: number;
}

/**
 * Ollama API provider for local inference
 */
export class OllamaProvider extends BaseProvider {
  constructor(config: ProviderConfig) {
    super(config);
  }

  protected getDefaultBaseUrl(): string {
    return "http://localhost:11434";
  }

  /**
   * Convert Anthropic messages to Ollama format
   */
  private convertToOllamaFormat(
    request: MessagesRequest,
    resolvedModel: string
  ): OllamaRequest {
    const messages: OllamaRequest["messages"] = [];

    // Add system prompt if present
    if (request.system) {
      const systemText =
        typeof request.system === "string"
          ? request.system
          : request.system
              .filter((b): b is TextContent => b.type === "text")
              .map((b) => b.text)
              .join("\n");

      messages.push({
        role: "system",
        content: systemText,
      });
    }

    // Convert messages
    for (const msg of request.messages) {
      let content: string;

      if (typeof msg.content === "string") {
        content = msg.content;
      } else {
        // Extract text content from blocks
        content = msg.content
          .filter((b): b is TextContent => b.type === "text")
          .map((b) => b.text)
          .join("\n");
      }

      messages.push({
        role: msg.role,
        content,
      });
    }

    return {
      model: resolvedModel,
      messages,
      stream: request.stream ?? false,
      options: {
        temperature: request.temperature,
        top_p: request.top_p,
        top_k: request.top_k,
        num_predict: request.max_tokens,
      },
    };
  }

  /**
   * Convert Ollama response to Anthropic format
   */
  private convertFromOllamaFormat(
    response: OllamaResponse,
    model: string
  ): MessagesResponse {
    return {
      id: `ollama-${Date.now()}`,
      type: "message",
      role: "assistant",
      content: [
        {
          type: "text",
          text: response.message.content,
        },
      ],
      model,
      stop_reason: "end_turn",
      stop_sequence: null,
      usage: {
        input_tokens: response.prompt_eval_count ?? 0,
        output_tokens: response.eval_count ?? 0,
      },
    };
  }

  /**
   * Send a non-streaming request to Ollama
   */
  async sendRequest(
    request: MessagesRequest,
    context: ProviderRequestContext
  ): Promise<MessagesResponse> {
    const startTime = Date.now();

    try {
      const url = `${this.baseUrl}/api/chat`;
      const ollamaRequest = this.convertToOllamaFormat(
        request,
        context.resolvedModel
      );
      ollamaRequest.stream = false;

      this.logger.debug("Sending request to Ollama", {
        url,
        model: context.resolvedModel,
      });

      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        context.timeoutMs
      );

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(ollamaRequest),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Ollama API error: ${response.status} - ${errorText}`);
      }

      const data = (await response.json()) as OllamaResponse;
      const latencyMs = Date.now() - startTime;

      this.recordRequest(true, latencyMs);
      this.logger.debug("Ollama request successful", { latencyMs });

      return this.convertFromOllamaFormat(data, context.resolvedModel);
    } catch (error) {
      const latencyMs = Date.now() - startTime;
      this.recordRequest(false, latencyMs);

      this.logger.error("Ollama request failed", {
        error: error instanceof Error ? error.message : "Unknown error",
        latencyMs,
      });

      throw error;
    }
  }

  /**
   * Send a streaming request to Ollama
   */
  async sendStreamingRequest(
    request: MessagesRequest,
    context: ProviderRequestContext,
    handler: StreamHandler
  ): Promise<void> {
    const startTime = Date.now();

    try {
      const url = `${this.baseUrl}/api/chat`;
      const ollamaRequest = this.convertToOllamaFormat(
        request,
        context.resolvedModel
      );
      ollamaRequest.stream = true;

      this.logger.debug("Starting streaming request to Ollama", {
        url,
        model: context.resolvedModel,
      });

      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        context.timeoutMs
      );

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(ollamaRequest),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Ollama API error: ${response.status} - ${errorText}`);
      }

      if (!response.body) {
        throw new Error("No response body for streaming request");
      }

      // Send initial message_start event (Anthropic SSE format)
      handler.onEvent(
        "message_start",
        JSON.stringify({
          type: "message_start",
          message: {
            id: `ollama-${Date.now()}`,
            type: "message",
            role: "assistant",
            content: [],
            model: context.resolvedModel,
            stop_reason: null,
            stop_sequence: null,
            usage: { input_tokens: 0, output_tokens: 0 },
          },
        })
      );

      // Send content_block_start
      handler.onEvent(
        "content_block_start",
        JSON.stringify({
          type: "content_block_start",
          index: 0,
          content_block: { type: "text", text: "" },
        })
      );

      // Process NDJSON stream from Ollama
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.trim()) continue;

          try {
            const data = JSON.parse(line) as OllamaResponse;

            if (data.message?.content) {
              // Convert to Anthropic content_block_delta format
              handler.onEvent(
                "content_block_delta",
                JSON.stringify({
                  type: "content_block_delta",
                  index: 0,
                  delta: {
                    type: "text_delta",
                    text: data.message.content,
                  },
                })
              );
            }

            if (data.done) {
              // Send final events
              handler.onEvent(
                "content_block_stop",
                JSON.stringify({
                  type: "content_block_stop",
                  index: 0,
                })
              );

              handler.onEvent(
                "message_delta",
                JSON.stringify({
                  type: "message_delta",
                  delta: {
                    stop_reason: "end_turn",
                    stop_sequence: null,
                  },
                  usage: {
                    output_tokens: data.eval_count ?? 0,
                  },
                })
              );

              handler.onEvent(
                "message_stop",
                JSON.stringify({
                  type: "message_stop",
                })
              );
            }
          } catch {
            // Skip malformed lines
            this.logger.debug("Skipping malformed Ollama response line");
          }
        }
      }

      const latencyMs = Date.now() - startTime;
      this.recordRequest(true, latencyMs);
      handler.onComplete();

      this.logger.debug("Ollama streaming request completed", { latencyMs });
    } catch (error) {
      const latencyMs = Date.now() - startTime;
      this.recordRequest(false, latencyMs);

      const err = error instanceof Error ? error : new Error("Unknown error");
      this.logger.error("Ollama streaming request failed", {
        error: err.message,
        latencyMs,
      });

      handler.onError(err);
    }
  }

  /**
   * Health check for Ollama
   */
  override async healthCheck(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${this.baseUrl}/api/tags`, {
        method: "GET",
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      this.recordHealthCheck(response.ok);
      return response.ok;
    } catch (error) {
      this.recordHealthCheck(
        false,
        error instanceof Error ? error.message : "Unknown error"
      );
      return false;
    }
  }
}

/**
 * Create an Ollama provider from config
 */
export function createOllamaProvider(config: ProviderConfig): OllamaProvider {
  return new OllamaProvider(config);
}
